import { NotificationBadge, type NotificationBadgeProps } from '@magicschool/spellbook/components/NotificationBadge';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { cn, mergeIds } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';
import { Slot as RadixSlot } from 'radix-ui/slot';
import type { ComponentProps, ReactNode } from 'react';
import { cloneElement, isValidElement } from 'react';

export const notificationAnchorBadgeVariants = cva(
  [
    'pointer-events-none',
    'absolute',
    'top-0',
    'end-0',
    '-translate-y-1/2',
    'translate-x-1/2',
    'rtl:-translate-x-1/2',
    'ring-2',
    'ring-surface-background-0',
  ],
  {
    variants: {
      /** What fills the corner: the default `NotificationBadge`, or custom content from `overrideDefault`, which needs its own shape. */
      content: {
        badge: [],
        custom: [
          'inline-flex',
          'rounded-full',
          'forced-colors:forced-color-adjust-none',
          'forced-colors:bg-[CanvasText]',
          'forced-colors:text-[Canvas]',
        ],
      },
    },
    defaultVariants: {
      content: 'badge',
    },
  },
);

/** `NotificationBadge`'s visual props; `size` defaults to `xxs`. The badge is `aria-hidden`, so its meaning goes in the anchor's `description`. */
export type NotificationAnchorBadgeProps = Omit<
  NotificationBadgeProps,
  'aria-label' | 'aria-labelledby' | 'aria-hidden' | 'aria-live' | 'role' | 'onClick'
>;

export const NotificationAnchorBadgeSlot = Slot<NotificationAnchorBadgeProps, ComponentProps<'span'> & NotificationAnchorBadgeProps>({
  displayName: 'NotificationAnchor.Badge',
  isRenderedByDefault: true,
  renderDefault: ({ size = 'xxs', className, ...props }) => (
    <NotificationBadge
      data-spellbook-name="notification-anchor-badge"
      size={size}
      {...props}
      className={cn(notificationAnchorBadgeVariants({ content: 'badge' }), className)}
      aria-hidden="true"
    />
  ),
  // Strips notificationBadgeVariants' axes, which mean nothing on a plain span; keep in sync with it.
  renderOverride: ({ size: _size, variant: _variant, pulse: _pulse, className, children, ...props }) => (
    <span
      data-spellbook-name="notification-anchor-badge"
      {...props}
      className={cn(notificationAnchorBadgeVariants({ content: 'custom' }), className)}
      aria-hidden="true"
    >
      {children}
    </span>
  ),
});

export type NotificationAnchorContentProps = {
  /** The single element the badge is pinned to. Put any props on it, not on `NotificationAnchor.Content`. */
  children: ReactNode;
};

type NotificationAnchorContentInjectedProps = {
  /** The props and `ref` the anchor forwards to the element. */
  anchorProps?: ComponentProps<typeof RadixSlot>;
  /** The id of the anchor's description, merged into `aria-describedby`. */
  anchorDescriptionId?: string;
};

export const NotificationAnchorContentSlot = Slot<NotificationAnchorContentProps & NotificationAnchorContentInjectedProps>({
  displayName: 'NotificationAnchor.Content',
  preventOverride: true,
  spellbookName: false,
  renderDefault: ({ anchorProps, anchorDescriptionId, children }) => {
    if (!isValidElement<{ 'aria-describedby'?: string }>(children)) return null;
    const describedBy = mergeIds(anchorProps?.['aria-describedby'], children.props['aria-describedby'], anchorDescriptionId);
    return <RadixSlot {...anchorProps}>{cloneElement(children, { 'aria-describedby': describedBy })}</RadixSlot>;
  },
});
